import { Subject, ReplaySubject } from 'rxjs';
import { filter } from 'rxjs/operators';
import { KeyCode } from '../../domain/key-code.enum';
import { ModalCloseReason } from './modal.domain';
import type { TemplateRef } from '@angular/core';
import type { GlobalPositionStrategy, OverlayRef, OverlaySizeConfig } from '@angular/cdk/overlay';
import type { Observable } from 'rxjs';
import type { ModalContainer, SubModalRef } from './modal.domain';
import type { ModalPosition } from './modal-options';
import type { ModalCloseEvent } from './components/modal-close/modal-close-button.directive';

/** Counter for unique modal ids. */
let uniqueId = 0;

export type CloseReasonType<R> = R | ModalCloseReason | undefined;

/**
 * A reference to a newly opened modal.
 *
 * Generic types:
 * T - input type (e.g., a component or template) being used as the modal's content,
 * R - return type (e.g., a string or enum) passed to the 'close' method.
 */
// eslint-disable-next-line @typescript-eslint/no-explicit-any
export class ModalRef<T, R = any> {

  /** The component instance opened in the modal. */
  componentInstance: T | null;

  /** Result to be passed to afterClosed. */
  private result: CloseReasonType<R>;

  readonly onCloseRequest: Subject<ModalCloseEvent> = new Subject<ModalCloseEvent>();

  private readonly _afterOpen: ReplaySubject<void> = new ReplaySubject(1);

  private readonly _afterClose: Subject<CloseReasonType<R>> = new Subject<CloseReasonType<R>>();

  private readonly _beforeClose: Subject<CloseReasonType<R>> = new Subject<CloseReasonType<R>>();

  private _isClosed: boolean = false;

  constructor(private overlayRef: OverlayRef,
              private readonly containerInstance: ModalContainer,
              readonly id: string = `td-wb-modal-${uniqueId++}`,
              private async?: boolean) {

    // Pass the id along to the container.
    containerInstance.id = this.id;
    containerInstance.ariaDescribedBy = containerInstance.ariaLabeledBy = `${this.id}-title`;
    // If the modal has a backdrop, handle clicks from the backdrop.
    if ((containerInstance.options.hasBackdrop ?? false) && (containerInstance.options.closeOnBackDropClick ?? false)) {
      this.overlayRef.backdropClick().subscribe((): void => this.handleClose(ModalCloseReason.BACKDROP_CLICK));
    }

    // Close when the escape key down event occurs.
    this.overlayRef.keydownEvents()
      .pipe(filter((event: KeyboardEvent): boolean =>
        event.key === KeyCode.ESC && (containerInstance.options.closeOnEscapeKey ?? false)))
      .subscribe((): void => this.handleClose(ModalCloseReason.ESC));

    this._afterOpen.next();
    this._afterOpen.complete();

    this.overlayRef.detachments().subscribe((): void => {
      if (!this._isClosed) {
        this._isClosed = true;
        this._beforeClose.next(this.result);
        this._beforeClose.complete();
      }
      this._afterClose.next(this.result);
      this._afterClose.complete();
      this.componentInstance = null;
      this.overlayRef.dispose();
    });
  }

  handleClose(result?: R | undefined | ModalCloseReason): void {
    const reason = result === '' ? ModalCloseReason.HEADER_BUTTON : result;
    if (this.async) {
      this.onCloseRequest.next({
        closeReason: reason,
        close: (closeReason: string): void => {
          this.close(closeReason as ModalCloseReason);
        }
      });
    } else {
      this.close(reason);
      this.onCloseRequest.next({
        closeReason: reason
      });
    }
  }

  /**
   * Close the modal.
   *
   * @param result Optional result to return to the modal opener.
   */
  close(result?: R | undefined | ModalCloseReason): void {
    this.result = result;

    if (!this._isClosed) {
      this._isClosed = true;
      this._beforeClose.next(result);
      this._beforeClose.complete();
    }
    this.overlayRef.detachBackdrop();
    this.overlayRef.detach();
  }

  /** Gets an observable that emits when the overlay's backdrop has been clicked. */
  backdropClick(): Observable<MouseEvent> {
    return this.overlayRef.backdropClick();
  }

  /**
   * Gets an observable that emits when keydown events are targeted on the overlay.
   */
  keydownEvents(): Observable<KeyboardEvent> {
    return this.overlayRef.keydownEvents();
  }

  /** Gets an observable that emits when modal is finished opening. */
  afterOpen(): Observable<void> {
    return this._afterOpen;
  }

  /** Gets an observable that emits when modal begins closing. */
  beforeClose(): Observable<R | string | undefined> {
    return this._beforeClose;
  }

  /** Gets an observable that emits when modal is finished closing. */
  afterClose(): Observable<R | string | undefined> {
    return this._afterClose;
  }

  /**
   * Updates the modal's width and height, defined, min, and max.
   *
   * @param size New size for the overlay.
   */
  updateSize(size: OverlaySizeConfig): ModalRef<T, R> {
    this.overlayRef.updateSize(size);
    return this;
  }

  /**
   * Updates the modal's position.
   *
   * @param position New position.
   */
  updatePosition(position?: ModalPosition): ModalRef<T, R> {
    const strategy = this.getPositionStrategy();

    if (position !== undefined && (position.left !== undefined || position.right !== undefined)) {
      if (position.left !== undefined) {
        strategy.left(position.left);
      } else {
        strategy.right(position.right);
      }
    } else {
      strategy.centerHorizontally();
    }

    if (position !== undefined && (position.top !== undefined || position.bottom !== undefined)) {
      if (position.top !== undefined) {
        strategy.top(position.top);
      } else {
        strategy.bottom(position.bottom);
      }
    } else {
      strategy.centerVertically();
    }
    this.overlayRef.updatePosition();
    return this;
  }

  getResult(): CloseReasonType<R> {
    return this.result;
  }

  openSubModal(template: TemplateRef<unknown>): SubModalRef {
    return this.containerInstance.openSubModal(template);
  }

  toggleScreenReaderVisibility(visible: boolean): void {
    this.containerInstance.toggleScreenReaderVisibility(visible);
  }

  getOverlayElement(): HTMLElement {
    return this.overlayRef.overlayElement;
  }

  /** Fetches the position strategy object from the overlay ref. */
  private getPositionStrategy(): GlobalPositionStrategy {
    return this.overlayRef.getConfig().positionStrategy as GlobalPositionStrategy;
  }
}
